<template>
  <img
    v-if="props.imageUrl"
    v-bind="$attrs"
    :src="displaySrc"
    :alt="resolvedAlt"
    @load="handleImageLoad"
    @error="handleImageError"
  />
  <img
    v-else-if="props.fallbackSrc"
    v-bind="$attrs"
    :src="props.fallbackSrc"
    :alt="resolvedAlt"
    @error="handleFallbackOnlyError"
    @load="handleFallbackOnlyLoad"
  />
  <div v-else class="image-placeholder"></div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, useAttrs } from "vue";

const props = defineProps({
  imageUrl: {
    type: String,
  },
  fallbackSrc: {
    type: String,
    default: "/images/no-profile.webp",
  },
  alt: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["image-loaded", "image-error"]);
const attrs = useAttrs();

const isOriginalBroken = ref(false);

const displaySrc = computed(() => {
  if (!props.imageUrl) {
    return props.fallbackSrc;
  }
  return isOriginalBroken.value ? props.fallbackSrc : props.imageUrl;
});

const resolvedAlt = computed(() => {
  return props.alt || attrs.alt || (props.imageUrl ? "Image" : "Placeholder");
});

const handleImageLoad = (event) => {
  if (event.target.src === props.imageUrl) {
    if (isOriginalBroken.value) {
      isOriginalBroken.value = false;
    }
  }
  emit("image-loaded", event.target.src);
};

const handleImageError = (event) => {
  const failedSrc = event.target.src;

  if (failedSrc === props.imageUrl && !isOriginalBroken.value) {
    isOriginalBroken.value = true;
    emit("image-error", { url: props.imageUrl, type: "original" });
  } else if (failedSrc === props.fallbackSrc && isOriginalBroken.value) {
    console.error("Fallback image also failed to load:", props.fallbackSrc);
    emit("image-error", { url: props.fallbackSrc, type: "fallback" });
  }
};

const handleFallbackOnlyError = (event) => {
  console.error(
    "Fallback image (no primary URL) failed to load:",
    event.target.src
  );
  emit("image-error", { url: event.target.src, type: "fallback-only" });
};
const handleFallbackOnlyLoad = (event) => {
  emit("image-loaded", event.target.src);
};

watch(
  () => props.imageUrl,
  (newUrl, oldUrl) => {
    if (newUrl && newUrl !== oldUrl) {
      isOriginalBroken.value = false;
    } else if (!newUrl && props.fallbackSrc) {
      isOriginalBroken.value = false;
    }
  }
);
</script>

<style scoped>
.image-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background-color: #f0f0f0;
  color: #ccc;
  border: 1px dashed #ccc;
  font-size: 0.8em;
  text-align: center;
}
img {
  object-fit: cover;
}
</style>
